import { z } from 'zod';
import { BIO_MAX_CHARS, BIO_MAX_WORDS, BIO_MIN_CHARS, LICENSE_BODY_NAME_MAX, PRACTICE_CITY_LABELS, countWords, } from './constants.js';
import { SESSION_TYPES, THERAPIST_SPECIALIZATIONS } from './types.js';
const sessionTypeEnum = z.enum(SESSION_TYPES);
/** Stored emails are always lowercase, even when the form was typed with capitals. */
export const emailSchema = z
    .string()
    .trim()
    .email()
    .max(255)
    .transform((value) => value.toLowerCase());
export const phoneSchema = z
    .string()
    .regex(/^\+?[1-9]\d{6,14}$/, 'Enter a phone number with the country code, for example +2348012345678');
/** Profile patch fields — empty string clears an optional phone value */
export const clearablePhoneFieldSchema = z.union([phoneSchema, z.literal('')]).optional();
export const nairaAmountSchema = z.number().int().min(100).max(10000000);
/** Query-string amounts (e.g. ?priceMax=5000) */
export const nairaAmountQuerySchema = z.coerce.number().int().min(100).max(10000000);
export const nanoidSchema = z.string().length(8);
export const bioTextSchema = z
    .string()
    .min(BIO_MIN_CHARS)
    .max(BIO_MAX_CHARS)
    .refine((value) => countWords(value) <= BIO_MAX_WORDS, {
    message: `Bio can be at most ${BIO_MAX_WORDS} words`,
});
export const licenseBodiesSchema = z
    .array(z.string().trim().min(2).max(LICENSE_BODY_NAME_MAX))
    .min(1)
    .max(6)
    .refine((bodies) => bodies.every((id) => id !== 'other'), {
    message: 'Choose a licensing body or type its name',
});
export const changePasswordSchema = z.object({
    currentPassword: z.string().min(1).max(128),
    newPassword: z.string().min(8).max(128),
});
export const sessionPackageInputSchema = z.object({
    title: z.string().trim().min(3).max(120),
    sessionCount: z.number().int().min(2).max(20),
    priceNgn: nairaAmountSchema,
});
export const registerClientSchema = z.object({
    email: emailSchema,
    phone: phoneSchema,
    password: z.string().min(8).max(128),
    firstName: z.string().trim().min(1).max(80),
    lastName: z.string().trim().min(1).max(80),
    preferredLanguage: z.string().min(2).max(10).default('en'),
    emergencyContact: phoneSchema.optional(),
    referralCode: nanoidSchema.optional(),
});
export const registerTherapistSchema = z.object({
    email: emailSchema,
    phone: phoneSchema,
    password: z.string().min(8).max(128),
    cassonNumber: z.string().min(5).max(50),
    licenseBody: z.string().default('CASSON'),
    licenseBodies: licenseBodiesSchema.optional(),
    specializations: z.array(z.enum(THERAPIST_SPECIALIZATIONS)).min(1).max(10).optional(),
    languages: z.array(z.string()).min(1).max(5).optional(),
    faithAffiliation: z.string().optional(),
    gender: z.enum(['male', 'female', 'non_binary', 'prefer_not_to_say']).optional(),
    bio: bioTextSchema.optional(),
    hourlyRateNgn: nairaAmountSchema.optional(),
});
export const loginSchema = z.object({
    email: emailSchema.optional(),
    phone: phoneSchema.optional(),
    password: z.string().min(1),
    rememberMe: z.boolean().optional(),
});
export const verifyOtpSchema = z.object({
    otp: z.string().length(6),
});
export const mfaVerifySchema = z.object({
    mfaSessionId: z.string().uuid(),
    otp: z.string().length(6),
});
export const submitAssessmentSchema = z.object({
    phq9Answers: z.array(z.number().int().min(0).max(3)).length(9),
    gad7Answers: z.array(z.number().int().min(0).max(3)).length(7),
});
export const createBookingSchema = z.object({
    therapistId: z.string().uuid(),
    sessionType: sessionTypeEnum,
    audience: z.enum(['individual', 'couples']).optional().default('individual'),
    packageId: z.string().uuid().optional(),
    scheduledAt: z.string().regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/),
});
export const payForBookingSchema = z.object({
    bookingId: z.string().uuid(),
    paystackReference: z.string().min(1),
});
export const initPaystackSchema = z.object({
    callbackUrl: z.string().url().max(500).optional(),
    /** Paystack checkout Cancel button redirect (`metadata.cancel_action`). */
    cancelUrl: z.string().url().max(500).optional(),
});
export const paymentOutcomeSchema = z.object({
    outcome: z.enum(['canceled', 'failed']),
    reference: z.string().min(1).max(255).optional(),
});
export const createGroupSessionSchema = z.object({
    therapistId: z.string().uuid(),
    sessionType: z.enum(['psychoeducation', 'support_group', 'skills_workshop']),
    maxParticipants: z.number().int().min(2).max(20).default(8),
    scheduledAt: z.string().regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/),
    priceNgn: nairaAmountSchema,
    title: z.string().min(5).max(200),
    description: z.string().min(20).max(2000),
});
export const therapistCreateGroupSessionSchema = createGroupSessionSchema.omit({
    therapistId: true,
});
export const saveSoapNoteSchema = z.object({
    bookingId: z.string().uuid(),
    subjective: z.string().min(1).max(5000),
    objective: z.string().min(1).max(5000),
    assessment: z.string().min(1).max(5000),
    plan: z.string().min(1).max(5000),
});
export const rateSessionSchema = z.object({
    bookingId: z.string().uuid(),
    rating: z.number().int().min(1).max(5),
    comment: z.string().max(2000).optional(),
});
export const therapistOnboardingSchema = z.object({
    displayName: z.string().min(2).max(100),
    gender: z.enum(['male', 'female', 'non_binary', 'prefer_not_to_say']),
    bio: bioTextSchema,
    faithAffiliation: z.string().max(100).optional(),
    cassonNumber: z.string().min(5).max(50),
    licenseBody: z.string().trim().min(2).max(LICENSE_BODY_NAME_MAX),
    licenseBodies: licenseBodiesSchema.optional(),
    specializations: z.array(z.enum(THERAPIST_SPECIALIZATIONS)).min(1).max(10),
    languages: z.array(z.string().min(2).max(50)).min(1).max(5),
    sessionModalities: z.array(sessionTypeEnum).min(1).max(3).optional(),
    hourlyRateNgn: nairaAmountSchema,
    couplesHourlyRateNgn: nairaAmountSchema.optional(),
    practiceCity: z.enum(PRACTICE_CITY_LABELS).optional(),
    yearsExperience: z.number().int().min(0).max(60).optional(),
    responseTimeHours: z.number().int().min(1).max(72).optional(),
    bankName: z.string().min(2).max(100),
    bankAccountNumber: z.string().regex(/^\d{10}$/),
    bankAccountName: z.string().min(2).max(100),
    availabilityDays: z.array(z.string()).min(1).max(7),
});
export const updateTherapistProfileSchema = z
    .object({
    bio: bioTextSchema.optional(),
    hourlyRateNgn: nairaAmountSchema.optional(),
    videoRateNgn: nairaAmountSchema.optional(),
    voiceRateNgn: nairaAmountSchema.optional(),
    chatRateNgn: nairaAmountSchema.optional(),
    couplesHourlyRateNgn: nairaAmountSchema.nullable().optional(),
    licenseBodies: licenseBodiesSchema.optional(),
    packages: z.array(sessionPackageInputSchema).max(6).optional(),
    yearsExperience: z.number().int().min(0).max(60).optional(),
    responseTimeHours: z.number().int().min(1).max(72).optional(),
    specializations: z.array(z.enum(THERAPIST_SPECIALIZATIONS)).min(1).max(10).optional(),
    languages: z.array(z.string().min(2).max(50)).min(1).max(5).optional(),
    sessionModalities: z.array(sessionTypeEnum).min(1).max(3).optional(),
    faithAffiliation: z.string().max(100).optional(),
    gender: z.enum(['male', 'female', 'non_binary', 'prefer_not_to_say']).optional(),
    introVideoUrl: z.string().url().max(500).optional(),
    practiceCity: z.enum(PRACTICE_CITY_LABELS).nullable().optional(),
    bankAccountName: z.string().min(2).max(100).optional(),
    bankAccountNumber: z
        .string()
        .regex(/^\d{10}$/)
        .optional(),
    bankName: z.string().min(2).max(100).optional(),
})
    .refine((data) => Object.values(data).some((v) => v !== undefined), {
    message: 'At least one field is required',
});
export const sendMessageSchema = z.object({
    bookingId: z.string().uuid(),
    content: z.string().min(1).max(10000),
    /** Client-minted once per send intent. Same id must not create a second message. */
    clientMessageId: z.string().uuid().optional(),
});
export const createStaffConversationSchema = z.object({
    participantId: z.string().uuid(),
});
export const sendStaffMessageSchema = z.object({
    content: z.string().min(1).max(10000),
});
export const updateProfileSchema = z.object({
    firstName: z.string().trim().max(80).optional(),
    preferredLanguage: z.string().min(2).max(10).optional(),
    phone: phoneSchema.optional(),
    emergencyContact: clearablePhoneFieldSchema,
    hmoMemberId: z.string().min(3).max(100).optional(),
});
export const createHmoKeySchema = z.object({
    hmoName: z.string().min(2).max(200),
    contactEmail: emailSchema,
    rateLimit: z.number().int().positive().default(1000),
});
export const apiPaginationSchema = z.object({
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(100).default(12),
});
export const corporateRegisterSchema = z.object({
    companyName: z.string().min(2).max(200),
    email: emailSchema,
    phone: phoneSchema,
    password: z.string().min(8).max(128),
    employeeEmailDomain: z.string().min(3).max(100),
});
export const referClientSchema = z.object({
    code: nanoidSchema,
});
export const therapistFilterSchema = z.object({
    specialization: z.enum(THERAPIST_SPECIALIZATIONS).optional(),
    gender: z.enum(['male', 'female', 'non_binary', 'prefer_not_to_say']).optional(),
    language: z.string().optional(),
    practiceCity: z.enum(PRACTICE_CITY_LABELS).optional(),
    faithAffiliation: z.string().optional(),
    priceMin: nairaAmountQuerySchema.optional(),
    priceMax: nairaAmountQuerySchema.optional(),
    availability: z.boolean().optional(),
    ratingMin: z.coerce.number().min(0).max(5).optional(),
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(100).default(12),
    search: z.string().trim().max(80).optional(),
});
export const mfaToggleSchema = z.object({
    password: z.string().min(8).max(128),
});
export const adminUserListSchema = z.object({
    search: z.string().optional(),
    role: z.enum(['client', 'therapist', 'corporate_hr', 'admin']).optional(),
    locked: z.coerce.boolean().optional(),
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(100).default(25),
});
export const adminLockUserSchema = z.object({
    reason: z.string().min(3).max(500),
});
export const adminResetPasswordSchema = z.object({
    password: z.string().min(8).max(128).optional(),
});
export const adminUpdateUserRoleSchema = z.object({
    role: z.enum(['client', 'therapist', 'corporate_hr', 'admin']),
});
export const rescheduleBookingSchema = z.object({
    scheduledAt: z.string().regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/),
});
const moodLevelEnum = z.enum(['awful', 'low', 'okay', 'good', 'great']);
export const journalEntrySchema = z.object({
    title: z.string().max(200).optional(),
    body: z.string().min(1).max(10000),
    mood: moodLevelEnum.optional(),
});
export const journalEntryUpdateSchema = journalEntrySchema
    .partial()
    .refine((data) => Object.values(data).some((v) => v !== undefined), {
    message: 'At least one field is required',
});
export const appGuideChatSchema = z.object({
    message: z.string().trim().min(1).max(500),
});
export const notificationPrefsSchema = z.object({
    sessionReminders: z.boolean().optional(),
    dailyAffirmations: z.boolean().optional(),
    marketingEmails: z.boolean().optional(),
    pushEnabled: z.boolean().optional(),
    messageAlerts: z.boolean().optional(),
    therapistUpdates: z.boolean().optional(),
    appUpdates: z.boolean().optional(),
});
export const adminWebSessionSettingsSchema = z.object({
    rememberDays: z.number().int().min(1).max(90),
    browserHours: z.number().int().min(1).max(168),
});
export const adminVideoProviderSchema = z.object({
    provider: z.enum(['daily', 'agora']),
});
export const adminBackupSettingsSchema = z.object({
    scheduleEnabled: z.boolean(),
    scheduleHourUtc: z.number().int().min(0).max(23),
    cadence: z.enum(['daily', 'weekly']),
    weekdayUtc: z.number().int().min(0).max(6),
    compress: z.boolean(),
    overwriteAfterDays: z.number().int().min(1).max(365),
});
export const adminReferralSettingsSchema = z.object({
    creditAmount: nairaAmountSchema,
    expiryDays: z.number().int().min(1).max(365),
});
export const adminWalletGrantSchema = z.object({
    clientEmail: emailSchema,
    amountNgn: nairaAmountSchema,
    note: z.string().max(500).optional(),
});
export const adminFeaturedTherapistSchema = z.object({
    isFeatured: z.boolean(),
    featuredSortOrder: z.number().int().min(0).max(999).optional(),
});
export const wellnessActivitySchema = z.object({
    slug: z.string().min(2).max(100),
    title: z.string().min(2).max(200),
    description: z.string().min(10).max(2000),
    category: z.enum(['meditation', 'breathing', 'mindfulness', 'journaling_prompt']),
    durationMinutes: z.number().int().min(1).max(120),
    instructions: z.string().min(10).max(5000),
    icon: z.string().max(10).optional(),
    isActive: z.boolean().optional(),
    sortOrder: z.number().int().min(0).max(999).optional(),
});
export const completeActivitySchema = z.object({
    durationSeconds: z.number().int().min(0).max(7200).optional(),
});
export const adminUpdateStaffRoleSchema = z.object({
    adminStaffRole: z.enum(['super_admin', 'user_manager', 'support', 'viewer']),
});
export const adminCreateStaffSchema = z.object({
    email: emailSchema,
    phone: phoneSchema,
    password: z.string().min(8).max(128),
    adminStaffRole: z.enum(['super_admin', 'user_manager', 'support', 'viewer']).default('support'),
});
export const adminCorporateListSchema = z.object({
    approved: z.coerce.boolean(),
});
export const adminCorporateAllowanceSchema = z.object({
    sessionAllowance: z.number().int().min(0).max(100_000),
});
export const hmoPaymentRequestSchema = z.object({
    bookingId: z.string().uuid(),
});
export const createCampusPartnershipSchema = z.object({
    universityName: z.string().min(2).max(200),
    emailDomain: z.string().min(3).max(100),
    subsidisedRateNgn: z.number().int().min(1).max(99),
    monthlyInvoice: z.number().int().min(0).optional(),
    active: z.boolean().optional(),
});
export const updateCampusPartnershipSchema = createCampusPartnershipSchema.partial();
const timeHHmm = z.string().regex(/^\d{2}:\d{2}$/);
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
export const weeklyAvailabilitySlotSchema = z.object({
    dayOfWeek: z.number().int().min(0).max(6),
    startTime: timeHHmm,
    endTime: timeHHmm,
    isAvailable: z.boolean(),
});
export const setWeeklyAvailabilitySchema = z.object({
    slots: z.array(weeklyAvailabilitySlotSchema).max(50),
});
export const calendarAvailabilitySlotSchema = z.object({
    date: isoDate,
    startTime: timeHHmm,
    endTime: timeHHmm,
    isAvailable: z.boolean().default(true),
    isBlocked: z.boolean().optional(),
});
export const setCalendarAvailabilitySchema = z.object({
    date: isoDate,
    isBlocked: z.boolean().optional(),
    ranges: z.array(z.object({ startTime: timeHHmm, endTime: timeHHmm })).max(10),
});
export const calendarAvailabilityRangeQuerySchema = z.object({
    from: isoDate,
    to: isoDate,
});
export const copyWeekAvailabilitySchema = z.object({
    weekStartDate: isoDate,
});
const expandParagraphsSchema = z.array(z.string().trim().min(1).max(2000)).max(10).default([]);
export const psychoeducationArticleBodySchema = z.object({
    title: z.string().trim().min(1).max(300),
    slug: z
        .string()
        .trim()
        .min(1)
        .max(300)
        .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/),
    summary: z.string().trim().min(1).max(2000),
    body: z.string().trim().min(1),
    expandParagraphs: expandParagraphsSchema.optional(),
    category: z.string().trim().min(1).max(100),
    language: z.string().trim().min(2).max(10),
    imageUrl: z.string().url().max(500).optional().or(z.literal('')),
    authorName: z.string().trim().min(1).max(150).optional(),
    publishedAt: z.string().datetime().nullable().optional(),
    featuredOnHome: z.boolean().optional(),
    homeSortOrder: z.number().int().min(0).max(99).nullable().optional(),
    kind: z.enum(['article', 'video', 'exercise']).optional(),
});
export const psychoeducationArticleUpdateSchema = psychoeducationArticleBodySchema.partial();
const hexColor = z.string().regex(/^#[0-9a-fA-F]{6}$/);
export const wellnessSlideBodySchema = z.object({
    key: z
        .string()
        .trim()
        .min(1)
        .max(50)
        .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/),
    caption: z.string().trim().min(1).max(300),
    imageUrl: z.string().url().max(500),
    gradient: z.tuple([hexColor, hexColor, hexColor]),
    isActive: z.boolean().optional(),
    libraryOrder: z.number().int().min(0).max(999).optional(),
});
export const wellnessSlideUpdateSchema = wellnessSlideBodySchema.partial();
export const wellnessPlacementUpdateSchema = z.object({
    slideIds: z.array(z.string().uuid()).max(20),
});
export const currencyRateUpdateSchema = z.object({
    rateToNgn: z.number().positive().max(1_000_000),
});
export const countryConfigUpdateSchema = z
    .object({
    name: z.string().trim().min(2).max(100).optional(),
    currency: z
        .string()
        .trim()
        .regex(/^[A-Za-z]{3}$/)
        .optional(),
    regulatoryStatus: z.enum(['active', 'pending_review', 'restricted']).optional(),
    smsProvider: z.string().trim().min(2).max(50).optional(),
    paymentProvider: z.string().trim().min(2).max(50).optional(),
})
    .refine((value) => Object.values(value).some((entry) => entry !== undefined), {
    message: 'At least one field is required',
});
export const translationSubmitSchema = z.object({
    questionIndex: z.number().int().min(0).max(500),
    questionText: z.string().trim().min(1).max(2000),
});
export const affirmationBodySchema = z.object({
    text: z.string().trim().min(1).max(500),
    author: z.string().trim().max(150).nullable().optional(),
    language: z.string().trim().min(2).max(10).optional(),
    isActive: z.boolean().optional(),
});
export const affirmationUpdateSchema = affirmationBodySchema
    .partial()
    .refine((value) => Object.values(value).some((entry) => entry !== undefined), {
    message: 'At least one field is required',
});
export const badgeDefinitionUpdateSchema = z
    .object({
    title: z.string().trim().min(1).max(200).optional(),
    description: z.string().trim().min(1).max(2000).optional(),
    targetCount: z.number().int().min(1).max(10_000).optional(),
    rewardLabel: z.string().trim().max(200).nullable().optional(),
    isActive: z.boolean().optional(),
    sortOrder: z.number().int().min(0).max(999).optional(),
})
    .refine((value) => Object.values(value).some((entry) => entry !== undefined), {
    message: 'At least one field is required',
});
export const wellnessActivityUpdateSchema = wellnessActivitySchema
    .omit({ slug: true })
    .partial()
    .refine((value) => Object.values(value).some((entry) => entry !== undefined), {
    message: 'At least one field is required',
});
export const siteCopyUpdateSchema = z.object({
    updates: z
        .array(z.object({
        key: z.string().min(1).max(100),
        value: z.string().min(1).max(2000),
    }))
        .min(1)
        .max(20),
});
//# sourceMappingURL=schemas.js.map